import WeatherApi from '@/api/config/weatherApi'
import { RootState } from '@/store/stores'
import { SearchParams } from '@/types'
import {
  TelemetryWeatherReportParams,
  WeatherReportParams,
} from '@/types/weather'
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  initialState,
  AssetData,
  WeatherDashboardState,
} from '@store/slices/DataComparison/types'
import interfaceAssetMappingApi from '@/api/interfaceSensor'
import telemetryApi from '@/api/telemetry'
import { isWeatherStation } from '@/utils/assetType'

export const fetchSensorData = createAsyncThunk(
  'weatherDashboard/fetchSensorData',
  async (
    params: {
      interfaceId: number
    },
    { rejectWithValue },
  ) => {
    try {
      const { data } = await interfaceAssetMappingApi.getAllByInterfaceId(
        Number(params.interfaceId),
      )

      const { data: allDevice } = await telemetryApi.getAllAsset()
      const mappingList = data ?? []
      const deviceList = allDevice ?? []
      const deviceMap = new Map(deviceList.map((d: any) => [d.id, d]))
      const mergedData = mappingList.map((mapping: any) => {
        const device = deviceMap.get(mapping.assetId)

        return {
          ...mapping,
          telemetryAssetId: mapping.assetId,
          deviceName: device?.name || null,
          topicName: device?.topicName || null,
          metadata: device?.metadata || {},

          deviceType: mapping?.type || device?.type || null,
        }
      })
      return {
        assetData: mergedData as AssetData[],
      }
    } catch (error) {
      return rejectWithValue(error)
    }
  },
)

export const fetchTagKeyData = createAsyncThunk(
  'weatherDashboard/fetchTagKeyData',
  async (
    params: {
      AssetId: number
    },
    { rejectWithValue },
  ) => {
    try {
      const data = await WeatherApi.getTagKey(params.AssetId)
      return {
        weatherStationId: params.AssetId,
        tagKeys: data as unknown as string[],
      }
    } catch (error) {
      return rejectWithValue(error)
    }
  },
)

function convertValueToMetric(value: number, columnName: string): number {
  return value
}

export const savedComparisonSlice = createSlice({
  name: 'diagnosticMapView',
  initialState,
  reducers: {
    setAssetId: (state, action: PayloadAction<{ assetId: number }>) => {
      state.assetId = action.payload.assetId
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchSensorData
      .addCase(fetchSensorData.pending, (state) => {
        state.isLoadingAssetData = true
        state.sensorTypes.isLoading = true
      })
      .addCase(fetchSensorData.fulfilled, (state, action) => {
        state.isLoadingAssetData = false

        if (action.payload.assetData) {
          const assetData = action.payload.assetData

          const uniqueTypes = [
            ...new Set(
              assetData.map((item: any) => item.metadata?.type || 'Other'),
            ),
          ]

          const sortedTypes = uniqueTypes.sort((a, b) => {
            const aStr = a as string
            const bStr = b as string
            const aIsOther = aStr === 'Other'
            const bIsOther = bStr === 'Other'
            if (aIsOther && !bIsOther) return 1
            if (bIsOther && !aIsOther) return -1
            return aStr.localeCompare(bStr)
          })

          const sensorTypes = sortedTypes.map((name, index) => ({
            id: index + 1,
            name,
          }))

          const otherTypeId =
            sensorTypes.find((t) => t.name === 'Other')?.id ?? null

          const assetDataWithType = assetData.map((asset: any) => {
            const typeName = asset.metadata?.type || 'Other'
            const matchedType = sensorTypes.find((t) => t.name === typeName)

            return {
              ...asset,
              sensorTypeId: matchedType?.id ?? otherTypeId,
            }
          })

          const weatherStations = assetDataWithType.filter(
            (item: any) => isWeatherStation(item),
          )

          state.sensorTypes.isLoading = false
          state.sensorTypes.data = sensorTypes
          state.assetData = assetDataWithType
          state.assetId = weatherStations[0]?.id || null
        }
      })
      .addCase(fetchSensorData.rejected, (state) => {
        state.isLoadingAssetData = false
        state.sensorTypes.isLoading = false
      })

      // fetchTagKey
      .addCase(fetchTagKeyData.pending, (state) => {
        state.isLoadingTagKeys = true
      })
      .addCase(fetchTagKeyData.fulfilled, (state, action) => {
        state.isLoadingTagKeys = false
        if (action.payload.tagKeys) {
          state.tagKeys[`${action.payload.weatherStationId}`] =
            action.payload.tagKeys
        }
      })
      .addCase(fetchTagKeyData.rejected, (state) => {
        state.isLoadingTagKeys = false
      })
  },
})

export const { setAssetId } = savedComparisonSlice.actions

// Explicit return type: inferring it from RootState is circular (RootState is
// built from this reducer), which editors can resolve to `unknown`
export const savedComparisonState = (state: RootState): WeatherDashboardState =>
  state.savedComparisonState

export default savedComparisonSlice.reducer
